import React from 'react';
import { Dialog as PloneDialog, Modal } from '@plone/components';
import { clsx } from 'clsx';
import './Dialog.scss';

export interface ESSDialogProps {
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  className?: string;
  children?: React.ReactNode;
  [key: string]: any; // Allow any props from @plone/components Dialog
}

/**
 * ESS Dialog Component
 *
 * A modal dialog component for displaying content in an overlay.
 * Wraps @plone/components Dialog with ESS design system styling.
 *
 * Note: This component wraps @plone/components Dialog in a styled div.
 * Dialog is typically used with Modal for overlay functionality.
 *
 * @example
 * ```tsx
 * import { ESSDialog } from './components/Molecules/Dialog';
 * import { Button } from './components/Atoms/Button';
 *
 * function MyComponent() {
 *   const [isOpen, setIsOpen] = useState(false);
 *
 *   return (
 *     <>
 *       <Button onPress={() => setIsOpen(true)}>
 *         Open Dialog
 *       </Button>
 *
 *       <ESSDialog
 *         size="md"
 *         isOpen={isOpen}
 *         onClose={() => setIsOpen(false)}
 *       >
 *         <h2>Dialog Title</h2>
 *         <p>Dialog content goes here</p>
 *       </ESSDialog>
 *     </>
 *   );
 * }
 * ```
 */
export const ESSDialog: React.FC<ESSDialogProps> = ({
  size = 'md',
  className,
  children,
  ...props
}) => {
  return (
    <div className={clsx('ess-dialog', `ess-dialog--${size}`, className)}>
      <PloneDialog {...props}>{children}</PloneDialog>
    </div>
  );
};

// Re-export Modal for convenience
export { Modal };

export default ESSDialog;
